import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import { useFrontoffWorkingMode } from '../hooks/useSystemStatus';
import { setLanguage } from '@digitaltattoo/redux-slices';
import { Save, Globe, Bell, Eye, Shield, Loader2, MessageSquare, Phone, CheckCircle, AlertCircle } from 'lucide-react';
import AccountLayout from '../components/AccountLayout';
import { fetchJSON } from '../services/http';

const API_URL = import.meta.env.VITE_API_URL || '';

interface SmsPreferences {
  smsConsent: boolean;
  smsConsentAt: string | null;
  phoneVerified: boolean;
  phoneVerifiedAt: string | null;
  reminderChannels: string[];
  phone: string | null;
  smsSystemEnabled: boolean;
}

interface PrivacySettings {
  allowPhotography: boolean;
  allowGalleryUse: boolean;
  allowTestimonials: boolean;
  allowMarketing: boolean;
}

export default function PreferencesPage() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const currentLanguage = useSelector((state: RootState) => state.language.current);
  const { isLimited, features } = useFrontoffWorkingMode();
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [privacyLoading, setPrivacyLoading] = useState(true);

  // SMS state
  const [smsPrefs, setSmsPrefs] = useState<SmsPreferences | null>(null);
  const [smsLoading, setSmsLoading] = useState(true);
  const [smsSaving, setSmsSaving] = useState(false);
  const [smsMessage, setSmsMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [otpSending, setOtpSending] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [showOtpInput, setShowOtpInput] = useState(false);

  // Preferences state
  const [preferences, setPreferences] = useState({
    language: currentLanguage,
    emailCommunications: true,
    smsCommunications: false,
    marketingEmails: false,
    appointmentReminders: true,
    promotionalOffers: false,
    emailDigest: 'weekly',
    theme: 'light',
    timezone: 'Europe/Paris',
    allowPhotography: false,
    allowGalleryUse: false,
    allowTestimonials: false,
    allowMarketing: false,
  });

  useEffect(() => {
    if (isLimited) {
      setPreferences((previous) => ({ ...previous, language: 'fr' }));
    }
  }, [isLimited]);

  useEffect(() => {
    loadSmsPreferences();
    loadPrivacyPreferences();
  }, []);

  const loadPrivacyPreferences = async () => {
    setPrivacyLoading(true);
    try {
      const response = await fetchJSON<{ success: boolean; data: PrivacySettings }>(
        `${API_URL}/api/client-auth/privacy-preferences`,
      );
      if (response.success) {
        setPreferences((previous) => ({ ...previous, ...response.data }));
      }
    } catch (error) {
      console.error('Failed to load privacy preferences:', error);
      setErrorMessage(t('auth.account.preferences.privacy.loadError'));
    } finally {
      setPrivacyLoading(false);
    }
  };

  const loadSmsPreferences = async () => {
    setSmsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/client-auth/sms-preferences`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setSmsPrefs(data.data);
        setPreferences((prev) => ({
          ...prev,
          smsCommunications: data.data.smsConsent,
        }));
      }
    } catch (err) {
      console.error('Failed to load SMS preferences:', err);
    } finally {
      setSmsLoading(false);
    }
  };

  const handleSmsConsentChange = async (consent: boolean) => {
    setSmsSaving(true);
    setSmsMessage(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/client-auth/sms-consent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          smsConsent: consent,
          reminderChannels: consent
            ? [...new Set([...(smsPrefs?.reminderChannels || ['email']), 'sms'])]
            : (smsPrefs?.reminderChannels || ['email']).filter((c) => c !== 'sms'),
        }),
      });
      const data = await res.json();
      if (data.success) {
        setSmsPrefs((prev) => prev ? { ...prev, ...data.data } : prev);
        setSmsMessage({ type: 'success', text: consent ? 'Consentement SMS activé' : 'Consentement SMS révoqué' });
        setTimeout(() => setSmsMessage(null), 3000);
      } else {
        setSmsMessage({ type: 'error', text: data.message || 'Erreur' });
      }
    } catch (err) {
      setSmsMessage({ type: 'error', text: 'Erreur réseau' });
    } finally {
      setSmsSaving(false);
    }
  };

  const handleReminderChannelChange = async (channels: string[]) => {
    setSmsSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/client-auth/sms-consent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ reminderChannels: channels }),
      });
      const data = await res.json();
      if (data.success) {
        setSmsPrefs((prev) => prev ? { ...prev, reminderChannels: channels } : prev);
      }
    } catch (err) {
      console.error('Failed to update reminder channels:', err);
    } finally {
      setSmsSaving(false);
    }
  };

  const requestPhoneOtp = async () => {
    setOtpSending(true);
    setSmsMessage(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/client-auth/phone-verify/request`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setShowOtpInput(true);
        setSmsMessage({ type: 'success', text: 'Code de vérification envoyé par SMS' });
        setTimeout(() => setSmsMessage(null), 5000);
      } else {
        setSmsMessage({ type: 'error', text: data.message || 'Échec de l\'envoi' });
      }
    } catch (err) {
      setSmsMessage({ type: 'error', text: 'Erreur réseau' });
    } finally {
      setOtpSending(false);
    }
  };

  const confirmPhoneOtp = async () => {
    if (!otpCode || otpCode.length !== 6) return;
    setOtpVerifying(true);
    setSmsMessage(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/client-auth/phone-verify/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ code: otpCode }),
      });
      const data = await res.json();
      if (data.success) {
        setSmsPrefs((prev) => prev ? { ...prev, phoneVerified: true, phoneVerifiedAt: new Date().toISOString() } : prev);
        setShowOtpInput(false);
        setOtpCode('');
        setSmsMessage({ type: 'success', text: 'Numéro de téléphone vérifié avec succès !' });
        setTimeout(() => setSmsMessage(null), 5000);
      } else {
        setSmsMessage({ type: 'error', text: data.message || 'Code invalide' });
      }
    } catch (err) {
      setSmsMessage({ type: 'error', text: 'Erreur réseau' });
    } finally {
      setOtpVerifying(false);
    }
  };

  const handleLanguageChange = (lang: 'en' | 'fr') => {
    setPreferences({ ...preferences, language: lang });
    i18n.changeLanguage(lang);
    dispatch(setLanguage(lang));
  };

  const handleToggle = (field: keyof typeof preferences) => {
    setPreferences({
      ...preferences,
      [field]: !preferences[field as keyof typeof preferences],
    });
  };

  const handleSelectChange = (field: keyof typeof preferences, value: string) => {
    setPreferences({
      ...preferences,
      [field]: value,
    });
  };

  const handleSave = async () => {
    setLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      await fetchJSON<{ success: boolean; data: PrivacySettings }>(
        `${API_URL}/api/client-auth/privacy-preferences`,
        {
          method: 'PUT',
          body: {
            allowPhotography: preferences.allowPhotography,
            allowGalleryUse: preferences.allowGalleryUse,
            allowTestimonials: preferences.allowTestimonials,
            allowMarketing: preferences.allowMarketing,
          },
        },
      );
      setSuccessMessage(t('auth.account.preferences.success.saved'));
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Failed to save preferences:', error);
      setErrorMessage(t('auth.account.preferences.privacy.saveError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AccountLayout>
      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-display font-bold text-textPrimary mb-2">
            {t('auth.account.preferences.title')}
          </h1>
          <p className="text-textSecondary">
            {t('auth.account.preferences.subtitle')}
          </p>
        </div>

        {successMessage && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-3">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <p className="text-sm text-green-800">{successMessage}</p>
          </div>
        )}

        {errorMessage && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-800">{errorMessage}</p>
          </div>
        )}

        <div className="columns-1 lg:columns-2 gap-6">
          {/* Language Preferences */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 break-inside-avoid mb-6 inline-block w-full">
            <div className="flex items-center space-x-3 mb-6">
              <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg">
                <Globe className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-textPrimary">
                  {t('auth.account.preferences.language.title')}
                </h2>
                <p className="text-sm text-textSecondary">
                  {t('auth.account.preferences.language.subtitle')}
                </p>
              </div>
            </div>

            {features.languageSwitch && <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                {t('auth.account.preferences.language.preferredLanguage')}
              </label>
              <div className="flex space-x-3">
                <button
                  onClick={() => handleLanguageChange('en')}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all ${
                    preferences.language === 'en'
                      ? 'border-primary-500 bg-primary-50 text-primary-700 font-medium'
                      : 'border-gray-200 bg-white text-textSecondary hover:border-gray-300'
                  }`}
                >
                  🇬🇧 English
                </button>
                <button
                  onClick={() => handleLanguageChange('fr')}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all ${
                    preferences.language === 'fr'
                      ? 'border-primary-500 bg-primary-50 text-primary-700 font-medium'
                      : 'border-gray-200 bg-white text-textSecondary hover:border-gray-300'
                  }`}
                >
                  🇫🇷 Français
                </button>
              </div>
            </div>}

            <div className="mt-4">
              <label className="block text-sm font-medium text-textPrimary mb-2">
                {t('auth.account.preferences.language.timezone')}
              </label>
              <select
                value={preferences.timezone}
                onChange={(e) => handleSelectChange('timezone', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="Europe/Paris">Paris (GMT+1)</option>
                <option value="Europe/London">London (GMT)</option>
                <option value="America/New_York">New York (GMT-5)</option>
                <option value="Asia/Tokyo">Tokyo (GMT+9)</option>
              </select>
            </div>
          </div>

          {/* Communication Preferences */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 break-inside-avoid mb-6 inline-block w-full">
            <div className="flex items-center space-x-3 mb-6">
              <div className="flex items-center justify-center w-10 h-10 bg-accent-100 rounded-lg">
                <Bell className="w-5 h-5 text-accent-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-textPrimary">
                  {t('auth.account.preferences.communication.title')}
                </h2>
                <p className="text-sm text-textSecondary">
                  {t('auth.account.preferences.communication.subtitle')}
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between py-3">
                <div>
                  <p className="font-medium text-textPrimary">
                    {t('auth.account.preferences.communication.emailCommunications')}
                  </p>
                  <p className="text-sm text-textSecondary">
                    {t('auth.account.preferences.communication.emailCommunicationsDesc')}
                  </p>
                </div>
                <button
                  onClick={() => handleToggle('emailCommunications')}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    preferences.emailCommunications ? 'bg-primary-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      preferences.emailCommunications ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between py-3 border-t border-gray-100">
                <div>
                  <p className="font-medium text-textPrimary">
                    {t('auth.account.preferences.communication.smsCommunications')}
                  </p>
                  <p className="text-sm text-textSecondary">
                    {t('auth.account.preferences.communication.smsCommunicationsDesc')}
                  </p>
                </div>
                <button
                  onClick={() => handleToggle('smsCommunications')}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    preferences.smsCommunications ? 'bg-primary-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      preferences.smsCommunications ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between py-3 border-t border-gray-100">
                <div>
                  <p className="font-medium text-textPrimary">
                    {t('auth.account.preferences.communication.appointmentReminders')}
                  </p>
                  <p className="text-sm text-textSecondary">
                    {t('auth.account.preferences.communication.appointmentRemindersDesc')}
                  </p>
                </div>
                <button
                  onClick={() => handleToggle('appointmentReminders')}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    preferences.appointmentReminders ? 'bg-primary-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      preferences.appointmentReminders ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between py-3 border-t border-gray-100">
                <div>
                  <p className="font-medium text-textPrimary">
                    {t('auth.account.preferences.communication.marketingEmails')}
                  </p>
                  <p className="text-sm text-textSecondary">
                    {t('auth.account.preferences.communication.marketingEmailsDesc')}
                  </p>
                </div>
                <button
                  onClick={() => handleToggle('marketingEmails')}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    preferences.marketingEmails ? 'bg-primary-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      preferences.marketingEmails ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between py-3 border-t border-gray-100">
                <div>
                  <p className="font-medium text-textPrimary">
                    {t('auth.account.preferences.communication.promotionalOffers')}
                  </p>
                  <p className="text-sm text-textSecondary">
                    {t('auth.account.preferences.communication.promotionalOffersDesc')}
                  </p>
                </div>
                <button
                  onClick={() => handleToggle('promotionalOffers')}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    preferences.promotionalOffers ? 'bg-primary-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      preferences.promotionalOffers ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div className="pt-3 border-t border-gray-100">
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  {t('auth.account.preferences.communication.emailDigest')}
                </label>
                <select
                  value={preferences.emailDigest}
                  onChange={(e) => handleSelectChange('emailDigest', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="daily">{t('auth.account.preferences.communication.daily')}</option>
                  <option value="weekly">{t('auth.account.preferences.communication.weekly')}</option>
                  <option value="monthly">{t('auth.account.preferences.communication.monthly')}</option>
                  <option value="never">{t('auth.account.preferences.communication.never')}</option>
                </select>
              </div>
            </div>
          </div>

          {/* SMS Preferences & Phone Verification */}
          {smsPrefs?.smsSystemEnabled && (
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 break-inside-avoid mb-6 inline-block w-full">
              <div className="flex items-center space-x-3 mb-6">
                <div className="flex items-center justify-center w-10 h-10 bg-green-100 rounded-lg">
                  <MessageSquare className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-textPrimary">
                    Notifications SMS
                  </h2>
                  <p className="text-sm text-textSecondary">
                    Recevez des rappels de rendez-vous par SMS
                  </p>
                </div>
              </div>

              {smsMessage && (
                <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 text-sm ${
                  smsMessage.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {smsMessage.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                  {smsMessage.text}
                </div>
              )}

              <div className="space-y-5">
                {/* Phone verification */}
                <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-gray-500" />
                      <div>
                        <p className="font-medium text-textPrimary">Vérification du téléphone</p>
                        <p className="text-sm text-textSecondary">
                          {smsPrefs?.phone || 'Aucun numéro enregistré'}
                        </p>
                      </div>
                    </div>
                    {smsPrefs?.phoneVerified ? (
                      <span className="flex items-center gap-1 text-sm text-green-600 font-medium">
                        <CheckCircle className="w-4 h-4" /> Vérifié
                      </span>
                    ) : (
                      <button
                        onClick={requestPhoneOtp}
                        disabled={otpSending || !smsPrefs?.phone}
                        className="btn-secondary text-sm py-1.5 px-3 flex items-center gap-1.5"
                      >
                        {otpSending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Phone className="w-3 h-3" />}
                        Vérifier
                      </button>
                    )}
                  </div>

                  {showOtpInput && !smsPrefs?.phoneVerified && (
                    <div className="mt-4 flex items-center gap-3">
                      <input
                        type="text"
                        value={otpCode}
                        onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="Code à 6 chiffres"
                        maxLength={6}
                        className="w-40 px-3 py-2 border border-gray-300 rounded-lg text-center text-lg tracking-widest font-mono focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                      <button
                        onClick={confirmPhoneOtp}
                        disabled={otpVerifying || otpCode.length !== 6}
                        className="btn-primary text-sm py-2 px-4 flex items-center gap-1.5"
                      >
                        {otpVerifying ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
                        Confirmer
                      </button>
                    </div>
                  )}
                </div>

                {/* SMS consent toggle */}
                <div className="flex items-center justify-between py-3">
                  <div>
                    <p className="font-medium text-textPrimary">Consentement SMS</p>
                    <p className="text-sm text-textSecondary">
                      J'accepte de recevoir des SMS transactionnels (rappels de rendez-vous, confirmations)
                    </p>
                    {smsPrefs?.smsConsentAt && smsPrefs.smsConsent && (
                      <p className="text-xs text-gray-400 mt-1">
                        Consentement donné le {new Date(smsPrefs.smsConsentAt).toLocaleDateString('fr-FR')}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => handleSmsConsentChange(!smsPrefs?.smsConsent)}
                    disabled={smsSaving}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      smsPrefs?.smsConsent ? 'bg-green-500' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        smsPrefs?.smsConsent ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Reminder channel selection */}
                {smsPrefs?.smsConsent && smsPrefs?.phoneVerified && (
                  <div className="pt-3 border-t border-gray-100">
                    <p className="font-medium text-textPrimary mb-2">Canaux de rappel</p>
                    <p className="text-sm text-textSecondary mb-3">
                      Choisissez comment recevoir vos rappels de rendez-vous
                    </p>
                    <div className="flex gap-3">
                      {[
                        { value: 'email', label: 'Email uniquement', channels: ['email'] },
                        { value: 'sms', label: 'SMS uniquement', channels: ['sms'] },
                        { value: 'both', label: 'Email + SMS', channels: ['email', 'sms'] },
                      ].map((option) => {
                        const isSelected = JSON.stringify(smsPrefs?.reminderChannels?.sort()) === JSON.stringify(option.channels.sort());
                        return (
                          <button
                            key={option.value}
                            onClick={() => handleReminderChannelChange(option.channels)}
                            disabled={smsSaving}
                            className={`flex-1 py-2.5 px-3 rounded-lg border-2 text-sm transition-all ${
                              isSelected
                                ? 'border-primary-500 bg-primary-50 text-primary-700 font-medium'
                                : 'border-gray-200 bg-white text-textSecondary hover:border-gray-300'
                            }`}
                          >
                            {option.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Info box */}
                <div className="p-3 rounded-lg bg-blue-50 border border-blue-100 text-sm text-blue-700">
                  <p className="font-medium mb-1">Protection de vos données (RGPD)</p>
                  <p>
                    Vos données de téléphone ne sont utilisées que pour les notifications transactionnelles liées à vos rendez-vous.
                    Vous pouvez révoquer votre consentement à tout moment. Aucun SMS marketing ne sera envoyé.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Privacy Preferences */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 break-inside-avoid mb-6 inline-block w-full">
            <div className="flex items-center space-x-3 mb-6">
              <div className="flex items-center justify-center w-10 h-10 bg-secondary-100 rounded-lg">
                <Shield className="w-5 h-5 text-secondary-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-textPrimary">
                  {t('auth.account.preferences.privacy.title')}
                </h2>
                <p className="text-sm text-textSecondary">
                  {t('auth.account.preferences.privacy.subtitle')}
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between gap-6 py-3">
                <div>
                  <p className="font-medium text-textPrimary">
                    {t('auth.account.preferences.privacy.allowPhotography')}
                  </p>
                  <p className="text-sm text-textSecondary">
                    {t('auth.account.preferences.privacy.allowPhotographyDesc')}
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={preferences.allowPhotography}
                  aria-label={t('auth.account.preferences.privacy.allowPhotography')}
                  onClick={() => handleToggle('allowPhotography')}
                  disabled={privacyLoading}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    preferences.allowPhotography ? 'bg-primary-500' : 'bg-gray-300'
                  } disabled:opacity-50`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      preferences.allowPhotography ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between gap-6 py-3 border-t border-gray-100">
                <div>
                  <p className="font-medium text-textPrimary">
                    {t('auth.account.preferences.privacy.allowGalleryUse')}
                  </p>
                  <p className="text-sm text-textSecondary">
                    {t('auth.account.preferences.privacy.allowGalleryUseDesc')}
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={preferences.allowGalleryUse}
                  aria-label={t('auth.account.preferences.privacy.allowGalleryUse')}
                  onClick={() => handleToggle('allowGalleryUse')}
                  disabled={privacyLoading}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    preferences.allowGalleryUse ? 'bg-primary-500' : 'bg-gray-300'
                  } disabled:opacity-50`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      preferences.allowGalleryUse ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between gap-6 py-3 border-t border-gray-100">
                <div>
                  <p className="font-medium text-textPrimary">
                    {t('auth.account.preferences.privacy.allowTestimonials')}
                  </p>
                  <p className="text-sm text-textSecondary">
                    {t('auth.account.preferences.privacy.allowTestimonialsDesc')}
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={preferences.allowTestimonials}
                  aria-label={t('auth.account.preferences.privacy.allowTestimonials')}
                  onClick={() => handleToggle('allowTestimonials')}
                  disabled={privacyLoading}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    preferences.allowTestimonials ? 'bg-primary-500' : 'bg-gray-300'
                  } disabled:opacity-50`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      preferences.allowTestimonials ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between gap-6 py-3 border-t border-gray-100">
                <div>
                  <p className="font-medium text-textPrimary">
                    {t('auth.account.preferences.privacy.allowMarketing')}
                  </p>
                  <p className="text-sm text-textSecondary">
                    {t('auth.account.preferences.privacy.allowMarketingDesc')}
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={preferences.allowMarketing}
                  aria-label={t('auth.account.preferences.privacy.allowMarketing')}
                  onClick={() => handleToggle('allowMarketing')}
                  disabled={privacyLoading}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    preferences.allowMarketing ? 'bg-primary-500' : 'bg-gray-300'
                  } disabled:opacity-50`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      preferences.allowMarketing ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Display Preferences */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 break-inside-avoid mb-6 inline-block w-full">
            <div className="flex items-center space-x-3 mb-6">
              <div className="flex items-center justify-center w-10 h-10 bg-indigo-100 rounded-lg">
                <Eye className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-textPrimary">
                  {t('auth.account.preferences.display.title')}
                </h2>
                <p className="text-sm text-textSecondary">
                  {t('auth.account.preferences.display.subtitle')}
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                {t('auth.account.preferences.display.theme')}
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => handleSelectChange('theme', 'light')}
                  className={`py-3 px-4 rounded-lg border-2 transition-all ${
                    preferences.theme === 'light'
                      ? 'border-primary-500 bg-primary-50 text-primary-700 font-medium'
                      : 'border-gray-200 bg-white text-textSecondary hover:border-gray-300'
                  }`}
                >
                  ☀️ {t('auth.account.preferences.display.light')}
                </button>
                <button
                  onClick={() => handleSelectChange('theme', 'dark')}
                  className={`py-3 px-4 rounded-lg border-2 transition-all ${
                    preferences.theme === 'dark'
                      ? 'border-primary-500 bg-primary-50 text-primary-700 font-medium'
                      : 'border-gray-200 bg-white text-textSecondary hover:border-gray-300'
                  }`}
                >
                  🌙 {t('auth.account.preferences.display.dark')}
                </button>
              </div>
              <p className="mt-2 text-xs text-textSecondary">
                {t('auth.account.preferences.display.themeNote')}
              </p>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={loading || privacyLoading}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>{t('auth.account.preferences.saving')}</span>
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                <span>{t('auth.account.preferences.saveChanges')}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </AccountLayout>
  );
}
